<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { state, importFiles } from './lib/store'
import { preloadOcr, setOcrProgressHandler } from './lib/ocr'
import { config } from './lib/config'
import { auth, signOut } from './lib/auth'
import { openUpgrade } from './lib/license'
import Toolbar from './components/Toolbar.vue'
import DocumentTree from './components/DocumentTree.vue'
import PdfViewer from './components/PdfViewer.vue'
import SnippetList from './components/SnippetList.vue'
import WelcomeView from './components/WelcomeView.vue'
import SignIn from './components/SignIn.vue'
import LicenseBadge from './components/LicenseBadge.vue'
import HelpPanel from './components/HelpPanel.vue'
import GettingStarted from './components/GettingStarted.vue'
import ReportDialog from './components/ReportDialog.vue'
import StatusBar from './components/StatusBar.vue'

const fileInput = ref<HTMLInputElement | null>(null)
const dragOver = ref(false)
const showHelp = ref(false)
const showReport = ref(false)
const showProfileMenu = ref(false)
const showWizard = ref(!localStorage.getItem('snipcell.wizardSeen'))
function closeWizard() {
  showWizard.value = false
  localStorage.setItem('snipcell.wizardSeen', '1')
}

// Auth. When config.requireAuth is true the app is gated behind sign-in. When false the app is
// usable without it, but the user can still sign in on demand via the header button.
const manualSignIn = ref(false)
const authLoading = computed(() => config.requireAuth && !auth.ready)
const showSignIn = computed(
  () => !auth.user && (config.requireAuth ? auth.ready : manualSignIn.value),
)
const dismissableSignIn = computed(() => !config.requireAuth)
watch(
  () => auth.user,
  (u) => {
    if (u) manualSignIn.value = false
  },
)

// Surface OCR progress (the first run downloads the ~2 MB Dutch language model).
onMounted(() => {
  setOcrProgressHandler((status, progress) => {
    if (!state.busy) return
    const pct = Math.round(progress * 100)
    state.status = `OCR: ${status}${progress > 0 && progress < 1 ? ` ${pct}%` : ''}`
  })
  // Paste an image from the clipboard (e.g. a screenshot) to import it as a snippable page.
  // Browsers only expose clipboard *images* here (not arbitrary files), which is the achievable
  // part of the original's paste feature.
  window.addEventListener('paste', (e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile()
        if (blob) {
          const file = new File([blob], blob.name || `pasted-${Date.now()}.png`, { type: blob.type })
          e.preventDefault()
          void importFiles([file]).then(() => preloadOcr())
        }
        break
      }
    }
  })
})

function pickFiles() {
  fileInput.value?.click()
}

async function onFiles(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files?.length) {
    await importFiles(input.files)
    // Warm the OCR engine in the background after the first import.
    void preloadOcr()
  }
  input.value = ''
}

async function onDrop(e: DragEvent) {
  dragOver.value = false
  if (e.dataTransfer?.files?.length) {
    await importFiles(e.dataTransfer.files)
    void preloadOcr()
  }
}
</script>

<template>
  <div
    class="app"
    :class="{ 'drag-over': dragOver }"
    @dragover.prevent="dragOver = true"
    @dragleave.prevent="dragOver = false"
    @drop.prevent="onDrop"
  >
    <div v-if="authLoading" class="auth-loading">
      <span class="spinner"></span> Restoring session…
    </div>

    <SignIn v-else-if="showSignIn" :dismissable="dismissableSignIn" @cancel="manualSignIn = false" />

    <template v-else>
      <header class="topbar">
        <img src="/assets/icon-32.png" alt="" class="brand-icon" />
        <span class="brand">SnipCell</span>
        <span class="badge">Reforged</span>
        <div class="spacer"></div>
        <button class="help-btn" title="Help &amp; FAQ" @click="showHelp = true">?</button>
        <LicenseBadge v-if="auth.user" />
        <div v-if="auth.user" class="profile">
          <button class="user" title="Account" @click="showProfileMenu = !showProfileMenu">
            {{ (auth.user.displayName || auth.user.email).charAt(0).toUpperCase() }}
          </button>
          <div v-if="showProfileMenu" class="menu" @click="showProfileMenu = false">
            <div class="who">
              <strong>{{ auth.user.displayName || 'Signed in' }}</strong>
              <span class="muted">{{ auth.user.email }}</span>
            </div>
            <button @click="openUpgrade()">Account &amp; billing</button>
            <button @click="showReport = true">Report a problem</button>
            <button class="danger" @click="signOut()">Sign out</button>
          </div>
          <div v-if="showProfileMenu" class="menu-backdrop" @click="showProfileMenu = false"></div>
        </div>
        <button v-else class="ghost" @click="manualSignIn = true">Sign in</button>
        <button class="primary" @click="pickFiles">＋ Import</button>
      </header>

      <template v-if="state.documents.length">
        <Toolbar />
        <DocumentTree />
        <PdfViewer />
        <SnippetList />
      </template>
      <WelcomeView v-else @import="pickFiles" />

      <StatusBar />
    </template>

    <input
      ref="fileInput"
      type="file"
      accept="application/pdf,image/*,.pdf,.docx,.png,.jpg,.jpeg,.bmp,.gif,.tif,.tiff,.webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      multiple
      hidden
      @change="onFiles"
    />

    <div v-if="dragOver" class="drop-hint">Drop to import</div>

    <HelpPanel
      v-if="showHelp"
      @close="showHelp = false"
      @wizard="showHelp = false; showWizard = true"
      @report="showHelp = false; showReport = true"
    />
    <GettingStarted v-if="showWizard" @close="closeWizard" />
    <ReportDialog v-if="showReport" @close="showReport = false" />
  </div>
</template>

<style scoped>
.app {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 280px;
}
.topbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(var(--bg), var(--bg-soft));
}
.brand-icon { width: 20px; height: 20px; }
.brand { font-weight: 700; font-size: 15px; }
.badge {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
  background: var(--brand);
  border-radius: 8px;
  padding: 1px 6px;
  text-transform: uppercase;
}
.ghost {
  font-size: 12px;
  padding: 4px 10px;
}
.help-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 0;
  font-weight: 700;
  color: var(--muted);
}
.profile { position: relative; }
.menu {
  position: absolute;
  top: 30px;
  right: 0;
  z-index: 70;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  min-width: 180px;
  padding: 4px;
  display: flex;
  flex-direction: column;
}
.menu .who { padding: 8px 10px; display: flex; flex-direction: column; border-bottom: 1px solid var(--line); margin-bottom: 4px; }
.menu .who span { font-size: 11px; }
.menu button { border: none; background: none; border-radius: 5px; text-align: left; padding: 7px 10px; font-size: 13px; }
.menu button:hover { background: var(--brand-50); }
.menu button.danger:hover { background: #fdeeee; color: var(--danger); }
.menu-backdrop { position: fixed; inset: 0; z-index: 65; }
.user {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  padding: 0;
  font-weight: 700;
  background: var(--brand);
  color: #fff;
  border-color: var(--brand);
}
.auth-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--muted);
}
.spinner {
  width: 13px;
  height: 13px;
  border: 2px solid var(--line);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.app.drag-over { outline: 3px dashed var(--brand); outline-offset: -3px; }
.drop-hint {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(43, 108, 176, 0.08);
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-dark);
  pointer-events: none;
}
</style>
